import moment from 'moment';
import { IntlShape, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';

import map from 'lodash/map';

import {
  getRequest,
  paginationMapper,
  resolveQueryString,
} from 'src/utils/request';
import { API } from 'src/reactQuery/Api';
import { daysUntilToday } from 'src/utils/datetime';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import { RequestDataArray } from 'src/types/requestData';
import messages from 'src/pages/ProjectDetailsPage/components/JobDetails/messages';

const AB_REASON_MESSAGES: Partial<
  Record<string, { id: string; defaultMessage: string }>
> = {
  missing_city: messages.abReasonMissingCity,
  time_conflict: messages.abReasonTimeConflict,
  group_not_in_run: messages.abReasonGroupNotInRun,
  no_active_contract: messages.abReasonNoActiveContract,
  low_behavior_score: messages.abReasonLowBehaviorScore,
  excluded_by_solver: messages.abReasonExcludedBySolver,
  missing_certificate: messages.abReasonMissingCertificate,
  weekly_limit_reached: messages.abReasonWeeklyLimitReached,
  yearly_limit_reached: messages.abReasonYearlyLimitReached,
  already_fully_booked: messages.abReasonShiftFullyBooked,
  rest_period_violation: messages.abReasonRestPeriodViolation,
  missing_language_requirement: messages.abReasonMissingLanguageRequirement,
};

export type JobApplicationType =
  | 'all'
  | 'accepted'
  | 'applied'
  | 'booked'
  | 'denied'
  | 'done';

const getJobApplications = async (
  jobUuid: string,
  type: JobApplicationType,
  filters: Record<string, string>,
  params: Record<string, string | number>,
  formatMessage: IntlShape['formatMessage'],
) => {
  const query = resolveQueryString([filters], params);

  const { data } = await getRequest<
    RequestDataArray<UCM.JobApplicationRawType, UCM.MetaRawType>
  >(API.GET.getJobApplications(jobUuid, type, query));

  return {
    meta: paginationMapper(data?.meta),
    data: mapJobApplications(data?.data, formatMessage),
  };
};

export const useGetJobApplications = ({
  jobUuid,
  type,
  params,
  filters,
  enabled = true,
}: {
  jobUuid: string;
  type: JobApplicationType;
  params: Record<string, string | number>;
  filters: Record<string, string>;
  enabled?: boolean;
}) => {
  const { formatMessage } = useIntl();

  return useQuery({
    queryKey: [QUERY_KEYS.JOB_APPLICATIONS, jobUuid, type, filters, params],
    queryFn: () =>
      getJobApplications(jobUuid, type, filters, params, formatMessage),
    staleTime: 1000 * 60 * 5,
    enabled,
  });
};

const mapJobApplications = (
  data: { attributes: UCM.JobApplicationRawType; id: string }[],
  formatMessage: IntlShape['formatMessage'],
): UCM.JobApplicationType[] => {
  if (!data) return [];

  return data.map(({ attributes, id }) => ({
    id,
    createdAt: attributes.created_at,
    validityScore: attributes.validity_score,
    lastRenewalAt: attributes.last_renewal_at,
    isFlex: attributes.is_flex,
    uuid: attributes.uuid,
    statuses: map(attributes.statuses, (data) => ({
      reason: {
        projectUuid: data.reason.project_uuid,
        jobId: data.reason.job_id,
        code: data.reason.code,
        title: data.reason.title,
      },
      isWaitlisted: data.is_waitlisted,
      status: data.status,
    })),
    aaSuggestedIds: attributes.aa_suggested_ids,
    aaSuggestedId: attributes.aa_suggested_id,
    aaSkippedId: attributes.aa_skipped_id,
    aaSkippedIds: attributes.aa_skipped_ids,
    abReasoning:
      attributes.ab_reasoning?.map((reason) => {
        const reasonMessage = AB_REASON_MESSAGES[reason.code];
        return {
          code: reason.code,
          text: reasonMessage ? formatMessage(reasonMessage) : reason.text,
        };
      }) ?? null,
    statusCode: attributes.status_code,
    favoriteCompany: attributes.favorite.company,
    favoriteManager: attributes.favorite.project_manager,
    appliedCount: attributes.applied_count,
    daysSinceApplied: daysUntilToday(attributes.applied_at),
    daysSinceAccepted: daysUntilToday(attributes.accepted_at),
    daysSinceBooked: daysUntilToday(attributes.booking_time_at),
    contractSummary: {
      status: attributes.contract_summary?.status,
      completed: attributes.contract_summary?.completely_signed,
      documentType: attributes.contract_summary?.document_type,
      contractType: attributes.contract_summary?.contract_type,
      initialized: attributes.contract_summary?.not_signed,
      userSigned: attributes.contract_summary?.user_signed,
      ucmSigned: attributes.contract_summary?.ucm_signed,
    },
    hasLongTermContract: attributes.has_long_term_contract,
    briefingReadAt: attributes.briefing_read_at,
    bookingTimeAt: attributes.booking_time_at,
    applicant: {
      id: attributes.applicant.id,
      uuid: attributes.applicant.uuid,
      fullName: attributes.applicant.full_name,
      languages: attributes.applicant.languages,
      ratingScore: attributes.applicant.rating_score,
      initialRatingScore: attributes.applicant.initial_rating_score,
      prescreeningRating: attributes.applicant.prescreening_rating,
      applicationsCancelledRatio:
        attributes.applicant.applications_cancelled_ratio,
      justifiedCancellationsRatio:
        attributes.applicant.justified_cancellations_ratio,
      confirmationTime: attributes.applicant.confirmation_time,
      status: {
        code: attributes.applicant.status.code,
        name: attributes.applicant.status.name,
      },
      lastLoginAt: moment(attributes.applicant.last_login_at)
        .locale('en')
        .fromNow(),
    },
    lastWorkInDays: attributes.last_work_in_days,
    yearlyWorkdaysCount: attributes.yearly_workdays_count,
  }));
};
